import { useRuntimeConfig } from '#app'
import { type ComputedRef, type Ref, computed } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { GetTechnicalSpecificationsResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/technical-specifications'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { pickBy } from '@backmarket/utils/object/pickBy'

import type { DisplayedReview } from '~/scopes/reviews/reviews-display/models/reviews'

function getDifferenceInDaysFromNow(endDate: string | undefined): number {
  if (endDate) {
    const start = new Date()
    const end = new Date(endDate)
    const oneDayTime = 1000 * 60 * 60 * 24

    const diff = (end.getTime() - start.getTime()) / oneDayTime

    return Math.floor(diff)
  }

  return 0
}

function getSanitizedSpecs(
  technicalSpecificationsResponse: Ref<GetTechnicalSpecificationsResponse | null>,
) {
  // We only want to keep those, if present
  const FIELDS = ['color', 'storage', 'mpn']

  const specs = technicalSpecificationsResponse.value?.specifications?.reduce(
    (acc, curr) =>
      FIELDS.includes(curr.field) && curr.values?.[0]?.label
        ? { ...acc, [curr.field]: curr.values?.[0]?.label }
        : acc,

    { color: null, storage: null, mpn: null },
  )

  return pickBy(specs, (spec) => spec !== null)
}

export function useProductHeadJsonLd({
  description,
  isOutOfStock,
  productResponse,
  ratesResponse,
  reviewsResponse,
  technicalSpecificationsResponse,
  selectedOfferResponse,
}: {
  description: ComputedRef<string>
  isOutOfStock: Ref<boolean>
  productResponse: Ref<GetProductResponse | null>
  ratesResponse: Ref<Rating | null>
  reviewsResponse: Ref<{ results: DisplayedReview[] } | null>
  technicalSpecificationsResponse: Ref<GetTechnicalSpecificationsResponse | null>
  selectedOfferResponse: Ref<GetPickersResponse['selectedOffer']>
}) {
  const runtimeConfig = useRuntimeConfig()
  const { features, market } = useMarketplace()

  const jsonLd = computed<Record<string, unknown> | null>(() => {
    if (!productResponse.value || isOutOfStock.value) {
      return null
    }

    return {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: productResponse.value.titles.raw,
      description: description.value,
      image: productResponse.value.images?.[0]?.url ?? null,
      ...getSanitizedSpecs(technicalSpecificationsResponse),
      ...insertIf(!!productResponse.value.brand, {
        brand: {
          '@type': 'Brand',
          name: productResponse.value.brand,
        },
      }),
      offers: {
        '@type': 'Offer',
        availability: 'http://schema.org/InStock',
        itemCondition: 'http://schema.org/RefurbishedCondition',
        ...insertIf(!isEmpty(selectedOfferResponse.value?.price), {
          priceCurrency: selectedOfferResponse.value?.price?.currency,
          price: selectedOfferResponse.value?.price?.amount,
        }),
        seller: {
          '@type': 'Organization',
          name: runtimeConfig.public.COMPANY_NAME,
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: market.countryCode,
          returnPolicyCategory:
            'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: features.coolingOffDays,
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/FreeReturn',
        },
        shippingDetails: [
          selectedOfferResponse.value?.shipping?.free,
          selectedOfferResponse.value?.shipping?.express,
        ]
          .filter((shippingMethod) => !!shippingMethod)
          .map((shippingMethod) => ({
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: shippingMethod.price?.amount || 0,
              currency: market.defaultCurrency,
            },
            shippingDestination: {
              '@type': 'DefinedRegion',
              addressCountry: market.countryCode,
            },
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: {
                '@type': 'QuantitativeValue',
                minValue: 1,
                maxValue: shippingMethod.delayInHours / 24,
                unitCode: 'DAY',
              },
              transitTime: {
                '@type': 'QuantitativeValue',
                minValue: getDifferenceInDaysFromNow(
                  shippingMethod?.earliestArrivalDate,
                ),
                maxValue: getDifferenceInDaysFromNow(
                  shippingMethod?.latestArrivalDate,
                ),
                unitCode: 'DAY',
              },
            },
          })),
      },
      ...insertIf(
        !isEmpty(ratesResponse.value) && ratesResponse.value?.count > 0,
        {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: ratesResponse.value?.averageRate,
            reviewCount: ratesResponse.value?.count,
            bestRating: 5,
            worstRating: 1,
          },
        },
      ),
      ...insertIf(!!reviewsResponse.value?.results.length, {
        review: reviewsResponse.value?.results.map((review) => ({
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.averageRate,
          },
          author: {
            '@type': 'Person',
            name: `${review.customer.firstName} ${review.customer.lastName}`,
          },
          reviewBody: review.translatedComment || review.comment,
        })),
      }),
    }
  })

  return { jsonLd }
}
